/* eslint-disable no-unused-vars */
import {
  MonitoringMonitoringProtocolClientStatus,
  PiracyClientModelChannelGroupEnum,
  PiracyCommonEnumsPerfPerfGroupByEnum,
  PiracyCommonEnumsProductTypeEnum,
  PiracyCommonEnumsSearchMethodEnum,
} from '~/swagger/data-contracts';

export interface IAsset {
  assetId: number;
  assetName: string;
}

export const enum RouteNameEnum {
  Performance = '/performance',
  AssetPortfolio = '/asset-portfolio',
  Enforcement = '/enforcement',
}

export const productTypeLabel: Record<PiracyCommonEnumsProductTypeEnum, string> = {
  [PiracyCommonEnumsProductTypeEnum.Webcomic]: 'Web Comics',
  [PiracyCommonEnumsProductTypeEnum.Webnovel]: 'Web Novel',
  [PiracyCommonEnumsProductTypeEnum.Game]: 'Game',
  [PiracyCommonEnumsProductTypeEnum.Adult]: 'Adult',
  [PiracyCommonEnumsProductTypeEnum.Sports]: 'Sports',
  [PiracyCommonEnumsProductTypeEnum.Publishing]: 'Publishing',
  [PiracyCommonEnumsProductTypeEnum.Music]: 'Music',
  [PiracyCommonEnumsProductTypeEnum.ELearning]: 'E-Learning',
  [PiracyCommonEnumsProductTypeEnum.Films]: 'Films & TV',
  [PiracyCommonEnumsProductTypeEnum.Animation]: 'Animation',
  [PiracyCommonEnumsProductTypeEnum.Software]: 'Software',
};

export const productList = Object.values(PiracyCommonEnumsProductTypeEnum).map((value) => ({
  value,
  label: productTypeLabel[value],
}));

export const groupByLabel = {
  [PiracyCommonEnumsPerfPerfGroupByEnum.Asset]: 'Asset',
  [PiracyCommonEnumsPerfPerfGroupByEnum.Domain]: 'Domain',
  [PiracyCommonEnumsPerfPerfGroupByEnum.SearchMethod]: 'Channel',
  [PiracyCommonEnumsPerfPerfGroupByEnum.None]: 'None',
} as const;

export const searchMethodLabel: Record<PiracyCommonEnumsSearchMethodEnum, string> = {
  [PiracyCommonEnumsSearchMethodEnum.Web]: 'Google Web',
  [PiracyCommonEnumsSearchMethodEnum.Image]: 'Google Image',
  [PiracyCommonEnumsSearchMethodEnum.Facebook]: 'Facebook',
  [PiracyCommonEnumsSearchMethodEnum.Instagram]: 'Instagram',
  [PiracyCommonEnumsSearchMethodEnum.Tiktok]: 'TikTok',
  [PiracyCommonEnumsSearchMethodEnum.Twitter]: 'Twitter',
  [PiracyCommonEnumsSearchMethodEnum.Douyin]: 'Douyin',
  [PiracyCommonEnumsSearchMethodEnum.Youtube]: 'YouTube',
  [PiracyCommonEnumsSearchMethodEnum.YoutubeShorts]: 'YouTube Shorts',
  [PiracyCommonEnumsSearchMethodEnum.VK]: 'VK',
  [PiracyCommonEnumsSearchMethodEnum.Dailymotion]: 'Dailymotion',
  [PiracyCommonEnumsSearchMethodEnum.Vimeo]: 'Vimeo',
  [PiracyCommonEnumsSearchMethodEnum.Twitch]: 'Twitch',
  [PiracyCommonEnumsSearchMethodEnum.Bilibili]: 'Bilibili',
  [PiracyCommonEnumsSearchMethodEnum.GooglePlay]: 'Google Play',
  [PiracyCommonEnumsSearchMethodEnum.AppleAppstore]: 'Apple Appstore',
  [PiracyCommonEnumsSearchMethodEnum.Telegram]: 'Telegram',
  [PiracyCommonEnumsSearchMethodEnum.Discord]: 'Discord',
};

export const channelGroupList = [
  PiracyClientModelChannelGroupEnum.SearchEngine,
  PiracyClientModelChannelGroupEnum.SocialMedia,
  PiracyClientModelChannelGroupEnum.AppStore,
  PiracyClientModelChannelGroupEnum.Others,
];

export const channelGroupLabel = {
  [PiracyClientModelChannelGroupEnum.SearchEngine]: 'Search Engine',
  [PiracyClientModelChannelGroupEnum.SocialMedia]: 'Social Media & Video Platforms',
  [PiracyClientModelChannelGroupEnum.AppStore]: 'App Stores',
  [PiracyClientModelChannelGroupEnum.Others]: 'Others',
} as const;

export const statusLabel = {
  [MonitoringMonitoringProtocolClientStatus.Detected]: 'Detected',
  [MonitoringMonitoringProtocolClientStatus.Confirmed]: 'Confirmed',
  [MonitoringMonitoringProtocolClientStatus.Reported]: 'Reported',
  [MonitoringMonitoringProtocolClientStatus.Enforced]: 'Enforced',
  [MonitoringMonitoringProtocolClientStatus.Denied]: 'Denied',
};

export const searchMethodByChannelGroup = {
  [PiracyClientModelChannelGroupEnum.SearchEngine]: [
    PiracyCommonEnumsSearchMethodEnum.Web,
    PiracyCommonEnumsSearchMethodEnum.Image,
  ],
  [PiracyClientModelChannelGroupEnum.SocialMedia]: [
    PiracyCommonEnumsSearchMethodEnum.Facebook,
    PiracyCommonEnumsSearchMethodEnum.Instagram,
    PiracyCommonEnumsSearchMethodEnum.Tiktok,
    PiracyCommonEnumsSearchMethodEnum.Twitter,
    PiracyCommonEnumsSearchMethodEnum.Douyin,
    PiracyCommonEnumsSearchMethodEnum.Youtube,
    PiracyCommonEnumsSearchMethodEnum.YoutubeShorts,
    PiracyCommonEnumsSearchMethodEnum.VK,
    PiracyCommonEnumsSearchMethodEnum.Dailymotion,
    PiracyCommonEnumsSearchMethodEnum.Vimeo,
    PiracyCommonEnumsSearchMethodEnum.Twitch,
    PiracyCommonEnumsSearchMethodEnum.Bilibili,
  ],
  [PiracyClientModelChannelGroupEnum.AppStore]: [
    PiracyCommonEnumsSearchMethodEnum.AppleAppstore,
    PiracyCommonEnumsSearchMethodEnum.GooglePlay,
  ],
  [PiracyClientModelChannelGroupEnum.Others]: [
    PiracyCommonEnumsSearchMethodEnum.Telegram,
    PiracyCommonEnumsSearchMethodEnum.Discord,
  ],
};

enum MaxFileSizeEnum {
  Image = 20971520,
  Document = 20971520,
  All = 20971520,
}

export enum DropzoneTypeEnum {
  Image = 'image',
  Document = 'document',
  All = 'all',
}

interface IDropzoneProps {
  acceptedFiles?: string[];
  maxFileSize?: number;
  getDropRejectMessage?: (rejectedFile: File, acceptedFiles: string[], maxFileSize: number) => string;
}

export const getDropzoneProps = (type: DropzoneTypeEnum, option?: IDropzoneProps): IDropzoneProps => {
  switch (type) {
    case DropzoneTypeEnum.Image:
      return {
        acceptedFiles: ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'],
        maxFileSize: MaxFileSizeEnum.Image,
        getDropRejectMessage: (rejectedFile, acceptedFiles, maxFileSize) => {
          if (!acceptedFiles.includes(rejectedFile.type)) {
            return `File ${rejectedFile.name} was rejected. File type has to be jpg, jpeg, png, or gif.`;
          } else {
            if (rejectedFile.size > maxFileSize) {
              return `File ${rejectedFile.name} was rejected. File size has to be smaller than ${
                maxFileSize / 1048576
              } megabytes.`;
            }
            return 'Unexpected error occurred';
          }
        },
        ...option,
      };
    case DropzoneTypeEnum.Document:
      return {
        acceptedFiles: ['application/pdf', 'image/jpg', 'image/jpeg', 'image/png', 'image/gif'],
        maxFileSize: MaxFileSizeEnum.Document,
        getDropRejectMessage: (rejectedFile, acceptedFiles, maxFileSize) => {
          if (!acceptedFiles.includes(rejectedFile.type)) {
            return `File ${rejectedFile.name} was rejected. File type has to be pdf, jpg, jpeg, png, or gif.`;
          } else {
            if (rejectedFile.size > maxFileSize) {
              return `File ${rejectedFile.name} was rejected. File size has to be smaller than ${
                maxFileSize / 1048576
              } megabytes.`;
            }
            return 'Unexpected error occurred';
          }
        },
        ...option,
      };
    case DropzoneTypeEnum.All:
      return {
        maxFileSize: MaxFileSizeEnum.All,
        getDropRejectMessage: (rejectedFile, acceptedFiles, maxFileSize) => {
          if (rejectedFile.size > maxFileSize) {
            return `File ${rejectedFile.name} was rejected. File size has to be smaller than ${
              maxFileSize / 1048576
            } megabytes.`;
          }
          return 'Unexpected error occurred';
        },
        ...option,
      };
  }
};
